import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { colorPalette } from "../styles/theme";

/**
 * 지표 및 대응 가이드 — SOP 문서 연동 인라인 도움말 (Slide-over Drawer, 폭 480px)
 * - 상단 바 [? 지표 및 대응 가이드] 버튼, 참가자 상세 [📖 지표 해석 가이드(SOP)] 버튼에서 같은 내용으로 열립니다.
 * - 백오피스 화면과 SOP 문서를 일치시키기 위해 아래 문구는 SOP 핵심 요약본 그대로 유지합니다.
 *   (문구를 바꿀 때는 SOP 문서와 함께 수정)
 */

const DRAWER_WIDTH = 480;

// 1. 디스트레스 4단계 표시 (정상 · 주의 · 경고 · 위험) — 구간 색상만 보여 주고 내부 절단점 수치는 노출하지 않음
const STAGES = [
  { label: "정상", color: "#22C55E" },
  { label: "주의", color: "#FACC15" },
  { label: "경고", color: "#F97316" },
  { label: "위험", color: "#EF4444" },
];

// 2. 위험 단계별 기관 담당자 권장 조치 (Action Guide) — SOP 원문
const ACTION_GUIDES = [
  {
    key: "crisis",
    tag: "🚨 긴급 위기 / 에피소드 진행 중",
    tagSx: { backgroundColor: "#B91C1C", color: "#FFFFFF" },
    body: (
      <>
        <b>확인 즉시(골든타임 내)</b> 유선 또는 대면으로 안부를 확인하고, 개인 상세 화면 우측 상단의 [검사 요청] 버튼을 통해
        PHQ-9/GAD-7 선별검사를 요청하거나 <b>전문 상담을 연계</b>하세요.
      </>
    ),
  },
  {
    key: "chronic",
    tag: "🔴 만성 고위험 구간 지속",
    tagSx: { backgroundColor: "#FEE2E2", color: "#B91C1C" },
    body: (
      <>
        급격한 변화(에피소드)가 없더라도 디스트레스 그래프가 지속적으로 주황/빨간색 영역에 머무는 참가자는{" "}
        <b>정기 상담 대상자로 지정</b>하여 관리하세요.
      </>
    ),
  },
  {
    key: "dropout",
    tag: "⚠️ 에피소드 발생 후 [잠잠] 전환",
    tagSx: { backgroundColor: "#FEF3C7", color: "#92400E" },
    body: (
      <>
        최근 에피소드(빨간 테두리)가 발생한 직후 활동 상태가 [잠잠](장기 미기록)으로 바뀐 참가자는 심리적 위축이나 고립 가능성이
        높으므로 <b>반드시 별도의 안부 연락</b>을 권장합니다.
      </>
    ),
  },
];

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 3.5 }}>
    <Typography sx={{ mb: 1.5, fontSize: 14, fontWeight: 700, color: colorPalette.textPrimary }}>
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const GuideCard = ({ title, visual, children }) => (
  <Box sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${colorPalette.border}`, backgroundColor: colorPalette.white }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
      {visual}
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.textPrimary }}>{title}</Typography>
    </Box>
    <Typography sx={{ fontSize: 13, color: colorPalette.textPrimary, lineHeight: 1.7 }}>{children}</Typography>
  </Box>
);

const SopGuideDrawer = ({ open, onClose }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    slotProps={{ paper: { sx: { width: { xs: "100%", sm: DRAWER_WIDTH }, borderRadius: 0, border: "none" } } }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderBottom: `1px solid ${colorPalette.border}`,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <HelpOutlineIcon sx={{ fontSize: 20, color: colorPalette.primary }} />
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: colorPalette.textPrimary }}>지표 및 대응 가이드</Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12, color: colorPalette.textSecondary }}>말랑포켓 SOP 핵심 요약</Typography>
        </Box>
      </Box>
      <IconButton onClick={onClose} size="small" aria-label="가이드 닫기">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>

    <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, backgroundColor: colorPalette.background }}>
      <Section number={1} title="말랑포켓 데이터 읽는 법">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <GuideCard title="증상·감정·디스트레스 추이">
            참가자가 남긴 감정 이모티콘과 일상 대화 속 스트레스/증상 신호를 AI가 종합하여 4단계 구간(정상 · 주의 · 경고 · 위험)으로
            시각화합니다.
            <Box component="span" sx={{ display: "flex", gap: 0.5, mt: 1 }}>
              {STAGES.map((stage) => (
                <Box
                  component="span"
                  key={stage.label}
                  sx={{
                    flex: 1,
                    py: 0.4,
                    borderRadius: 1,
                    textAlign: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    color: colorPalette.textPrimary,
                    backgroundColor: `${stage.color}33`,
                    borderTop: `3px solid ${stage.color}`,
                  }}
                >
                  {stage.label}
                </Box>
              ))}
            </Box>
          </GuideCard>
          <GuideCard
            title="에피소드 (빨간 테두리 표시)"
            visual={
              <Box component="svg" width={18} height={18} sx={{ flexShrink: 0 }}>
                <circle cx="9" cy="9" r="6.5" fill="#8b5cf6" stroke="#ef4444" strokeWidth="2.5" />
              </Box>
            }
          >
            참가자의 상태가 본인의 평소 기준선에서 크게 벗어나 급격히 악화되었거나, 대화 중 즉각적인 확인이 필요한 안전 주의 신호가
            감지되었음을 뜻합니다.
          </GuideCard>
        </Box>
      </Section>

      <Section number={2} title="위험 단계별 기관 담당자 권장 조치 (Action Guide)">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {ACTION_GUIDES.map((guide) => (
            <Box
              key={guide.key}
              sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${colorPalette.border}`, backgroundColor: colorPalette.white }}
            >
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  px: 1,
                  py: 0.4,
                  borderRadius: 1,
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  ...guide.tagSx,
                }}
              >
                {guide.tag}
              </Box>
              <Typography sx={{ mt: 1, fontSize: 13, color: colorPalette.textPrimary, lineHeight: 1.7 }}>{guide.body}</Typography>
            </Box>
          ))}
        </Box>
      </Section>

      <Typography sx={{ fontSize: 11, color: colorPalette.textSecondary }}>
        ※ 본 내용은 기관 SOP 문서의 핵심 요약본입니다. 세부 절차는 SOP 원문을 따릅니다.
      </Typography>
    </Box>
  </Drawer>
);

export default SopGuideDrawer;
